import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import { 
  Scan, Loader2, Calendar, Clock, MapPin, User, FileText, 
  CheckCircle, AlertTriangle, Edit, Scale, Gavel
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type options
const eventTypeOptions = [
  { value: 'court_date', label: 'Court Date' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'trial', label: 'Trial' },
  { value: 'deposition', label: 'Deposition' },
  { value: 'mediation', label: 'Mediation' },
  { value: 'client_meeting', label: 'Client Meeting' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'other', label: 'Other' }
];

export default function NoticeScannerDialog({ 
  document, 
  caseId, 
  onClose, 
  onEventCreated 
}) {
  const { token } = useAuth();
  
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [draftEvent, setDraftEvent] = useState(null);
  const [caseMatch, setCaseMatch] = useState(null);
  
  // Editable event form
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'hearing',
    start_datetime: '',
    end_datetime: '',
    location: '',
    sync_to_calendar: true
  });
  
  const [confirming, setConfirming] = useState(false);

  // Scan the document
  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/documents/${document.id}/scan-notice`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (res.ok) {
        const data = await res.json();
        setExtractedInfo(data.extracted_info);
        setDraftEvent(data.draft_event);
        setCaseMatch(data.case_match);
        setScanned(true);
        
        // Pre-fill form if draft event was created
        if (data.draft_event) {
          setEventForm({
            title: data.draft_event.title || '',
            description: data.draft_event.description || '',
            event_type: data.draft_event.event_type || 'hearing',
            start_datetime: data.draft_event.start_datetime?.slice(0, 16) || '',
            end_datetime: data.draft_event.end_datetime?.slice(0, 16) || '',
            location: data.draft_event.location || '',
            sync_to_calendar: true
          });
        }
        
        toast.success('Document scanned successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to scan document');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan document');
    } finally {
      setScanning(false);
    }
  };

  // Confirm the draft event
  const handleConfirmEvent = async () => {
    if (!draftEvent) return;
    
    setConfirming(true);
    try {
      const formData = new FormData();
      formData.append('title', eventForm.title);
      formData.append('description', eventForm.description);
      formData.append('event_type', eventForm.event_type);
      formData.append('start_datetime', new Date(eventForm.start_datetime).toISOString());
      formData.append('end_datetime', new Date(eventForm.end_datetime).toISOString());
      formData.append('location', eventForm.location);
      formData.append('sync_to_calendar', eventForm.sync_to_calendar);

      const res = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/events/${draftEvent.id}/confirm`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (res.ok) {
        const data = await res.json();
        toast.success('Event confirmed and added to calendar');
        
        if (data.calendar_sync?.created) {
          toast.success('Event synced to your calendar');
        }
        
        if (onEventCreated) onEventCreated();
        onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to confirm event');
      }
    } catch (error) {
      toast.error('Failed to confirm event');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-blue-600" />
            Scan for Notice Information
          </DialogTitle>
          <DialogDescription>
            Extract hearing information from {document?.original_filename}
          </DialogDescription>
        </DialogHeader>

        {/* Initial Scan State */}
        {!scanned && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-medium text-lg mb-2">Ready to Scan</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              This will analyze the document using AI to extract hearing notice information 
              such as court dates, times, locations, and parties.
            </p>
            <Button onClick={handleScan} disabled={scanning} size="lg" data-testid="start-scan-btn">
              {scanning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scanning Document...
                </>
              ) : (
                <>
                  <Scan className="w-4 h-4 mr-2" />
                  Scan for Notice Information
                </>
              )}
            </Button>
          </div>
        )}

        {/* Scanned Results */}
        {scanned && extractedInfo && (
          <div className="space-y-4">
            {/* Case Number Match Alert */}
            {caseMatch && caseMatch.notice_case_number && (
              <Alert variant={caseMatch.matches ? "default" : "destructive"}>
                {caseMatch.matches ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <AlertTitle>
                  {caseMatch.matches ? 'Case Number Matches' : 'Case Number Mismatch'}
                </AlertTitle>
                <AlertDescription>
                  Notice case number: <strong>{caseMatch.notice_case_number}</strong>
                  <br />
                  Current case number: <strong>{caseMatch.current_case_number}</strong>
                </AlertDescription>
              </Alert>
            )}

            {/* Document Type Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Document Type:</span>
              <Badge variant="outline" className="capitalize">
                {extractedInfo.document_type?.replace(/_/g, ' ') || 'Unknown'}
              </Badge>
              {extractedInfo.has_future_hearing && (
                <Badge className="bg-green-100 text-green-700">
                  <Calendar className="w-3 h-3 mr-1" />
                  Future Hearing Detected
                </Badge>
              )}
            </div>

            {/* Extracted Information Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Extracted Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {extractedInfo.hearing_info && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground">Hearing Type:</span>
                        <p className="font-medium capitalize">
                          {extractedInfo.hearing_info.hearing_type?.replace(/_/g, ' ') || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Purpose:</span>
                        <p className="font-medium">{extractedInfo.hearing_info.hearing_purpose || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Date:
                        </span>
                        <p className="font-medium">{extractedInfo.hearing_info.date || 'Not found'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Time:
                        </span>
                        <p className="font-medium">{extractedInfo.hearing_info.time || 'Not found'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Scale className="w-3 h-3" /> Court:
                        </span>
                        <p className="font-medium">{extractedInfo.hearing_info.court_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Gavel className="w-3 h-3" /> Judge:
                        </span>
                        <p className="font-medium">{extractedInfo.hearing_info.judge_name || 'N/A'}</p>
                      </div>
                    </div>
                    
                    {extractedInfo.hearing_info.courtroom && (
                      <div>
                        <span className="text-muted-foreground">Courtroom/Dept:</span>
                        <p className="font-medium">{extractedInfo.hearing_info.courtroom}</p>
                      </div>
                    )}
                  </>
                )}
                
                {extractedInfo.parties && (
                  <div className="pt-2 border-t">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <User className="w-3 h-3" /> Parties:
                    </span>
                    <div className="mt-1 space-y-1">
                      {extractedInfo.parties.addressed_to && (
                        <p><span className="text-muted-foreground">To:</span> {extractedInfo.parties.addressed_to}</p>
                      )}
                      {extractedInfo.parties.plaintiff_petitioner && (
                        <p><span className="text-muted-foreground">Plaintiff/Petitioner:</span> {extractedInfo.parties.plaintiff_petitioner}</p>
                      )}
                      {extractedInfo.parties.defendant_respondent && (
                        <p><span className="text-muted-foreground">Defendant/Respondent:</span> {extractedInfo.parties.defendant_respondent}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {extractedInfo.additional_info && (
                  <div className="pt-2 border-t">
                    <span className="text-muted-foreground">Additional Notes:</span>
                    <p className="mt-1">{extractedInfo.additional_info}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Editable Event Form (only if future hearing detected) */}
            {draftEvent && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Draft Event Created
                  </CardTitle>
                  <CardDescription>
                    Review and edit the event details before confirming
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Event Title</Label>
                    <Input
                      id="title"
                      value={eventForm.title}
                      onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                      data-testid="event-title-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event_type">Event Type</Label>
                      <Select
                        value={eventForm.event_type}
                        onValueChange={(v) => setEventForm(prev => ({ ...prev, event_type: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypeOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={eventForm.location}
                        onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_datetime">Start Date & Time</Label>
                      <Input
                        id="start_datetime"
                        type="datetime-local"
                        value={eventForm.start_datetime}
                        onChange={(e) => setEventForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                        data-testid="event-start-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_datetime">End Date & Time</Label>
                      <Input
                        id="end_datetime"
                        type="datetime-local"
                        value={eventForm.end_datetime}
                        onChange={(e) => setEventForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={eventForm.description}
                      onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sync_calendar"
                      checked={eventForm.sync_to_calendar}
                      onCheckedChange={(checked) => setEventForm(prev => ({ ...prev, sync_to_calendar: checked }))}
                    />
                    <Label htmlFor="sync_calendar" className="cursor-pointer">
                      Sync to my calendar
                    </Label>
                  </div>
                  
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Document "{document?.original_filename}" will be attached to this event
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Future Hearing Notice */}
            {!extractedInfo.has_future_hearing && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Future Hearing Date Detected</AlertTitle>
                <AlertDescription>
                  This appears to be a {extractedInfo.document_type?.replace(/_/g, ' ')} 
                  without a future hearing date. No calendar event will be created.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {scanned ? 'Close' : 'Cancel'}
          </Button>
          {scanned && draftEvent && (
            <Button 
              onClick={handleConfirmEvent} 
              disabled={confirming}
              data-testid="confirm-event-btn"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm & Create Event
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
